'use client';
import type Lenis from 'lenis';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { homePageData } from '../utils/page-data/homePage';
import Button from '../ui/Button';
import Image from 'next/image';

interface Heading {
  id: string;
  text: string;
}

interface TableOfContentsProps {
  headings: Heading[];
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ headings }) => {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -80% 0px',
        threshold: 0,
      },
    );

    // Observe all h2 elements
    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      headings.forEach(({ id }) => {
        const element = document.getElementById(id);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [headings]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();

    const element = document.getElementById(id);

    if (element) {
      const lenis = (window as unknown as { appLenis?: Lenis }).appLenis;

      if (lenis) {
        lenis.scrollTo(element, { offset: -120, lock: true, force: true });
      } else {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 120;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }

      setActiveId(id);
      window.history.pushState(null, '', `#${id}`);
    }
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <div className="sticky top-20 flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h3 className="mb-4 font-opt text-2xl font-bold text-black">
          Table of Contents
        </h3>
        <nav>
          <ul className="space-y-2.5">
            {headings.map((heading) => (
              <li key={heading.id}>
                <Link
                  href={`#${heading.id}`}
                  onClick={(e) => handleClick(e, heading.id)}
                  className={`line-clamp-2 flex items-start gap-1.5 font-opt text-[15px] font-normal transition-colors ${
                    activeId === heading.id
                      ? 'font-medium text-lightBlue'
                      : 'text-gray-800 hover:text-lightBlue'
                  }`}
                >
                  <Image
                    src="/images/arrow-circle.png"
                    alt="arrow-circle"
                    width={20}
                    height={20}
                    className="mt-0.5"
                  />
                  {heading.text}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
      <div
        className="mt-5 rounded-xl bg-tech-we-work bg-cover bg-no-repeat"
        style={{ backgroundColor: 'black' }}
      >
        <motion.div
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left - rect.width / 2;
            const y = e.clientY - rect.top - rect.height / 2;

            e.currentTarget.style.transform = `
              perspective(1000px) 
              rotateX(${-y / 60}deg) 
              rotateY(${x / 60}deg) 
              scale(1.02)
            `;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = `
              perspective(1000px) 
              rotateX(0deg) 
              rotateY(0deg) 
              scale(1)
            `;
          }}
          style={{
            transition: 'transform 0.3s ease-out',
          }}
          className="group relative overflow-hidden"
        >
          <div className="pointer-events-none absolute inset-0 left-1/2 top-0 z-40 hidden lg:block">
            <div
              className={`absolute bottom-1 h-[30vw] w-[30vw] scale-100 rounded-full border-1 border-white/100 transition-transform delay-0 duration-500 ease-out group-hover:scale-[0.95]`}
            ></div>
            <div
              className={`absolute bottom-1 h-[30vw] w-[30vw] scale-[1.1] rounded-full border-1 border-white/75 transition-transform delay-100 duration-500 ease-out group-hover:scale-[1.05]`}
            ></div>
            <div
              className={`absolute bottom-1 h-[30vw] w-[30vw] scale-[1.2] rounded-full border-1 border-white/50 transition-transform delay-200 duration-500 ease-out group-hover:scale-[1.15]`}
            ></div>
            <div
              className={`absolute bottom-1 h-[30vw] w-[30vw] scale-[1.3] rounded-full border-1 border-white/25 transition-transform delay-300 duration-500 ease-out group-hover:scale-[1.25]`}
            ></div>
            <div
              className={`absolute bottom-1 h-[30vw] w-[30vw] scale-[1.4] rounded-full border-1 border-white/10 transition-transform delay-500 duration-500 ease-out group-hover:scale-[1.35]`}
            ></div>
          </div>
          <div
            className={`flex h-full flex-col justify-between gap-14 rounded-[17px] p-5 md:px-[28px] md:py-[32px]`}
          >
            <p
              className={`font-opt text-2xl font-extrabold leading-8 text-white md:text-3xl md:leading-9 4xl:text-[40px] 4xl:leading-[45px]`}
            >
              {homePageData?.contactUsCardTitle}
            </p>
            <Button
              btnName={homePageData?.contactUsCardBtn}
              redirectionLink={homePageData?.contactUsCardBtnLink}
              isBackgroundLight={true}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TableOfContents;
